/** Public deployment settings. Production must load these before importing the app. */
export interface UiRuntimeConfig {
  schemaVersion: 1;
  identityApiBase: string;
  oidcAuthority: string;
  oidcClientId: string;
  oidcScope?: string;
  oidcRedirectUri?: string;
  oidcPostLogoutRedirectUri?: string;
  identityManagementUrl?: string;
}

export const RUNTIME_CONFIG_PATH = '/runtime-config.json';
let loadedConfig: UiRuntimeConfig | undefined;

export function parseRuntimeConfig(value: unknown): UiRuntimeConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid UI configuration.');
  }
  const config = value as Record<string, unknown>;
  const required = ['identityApiBase', 'oidcAuthority', 'oidcClientId'];
  const optional = ['oidcScope', 'oidcRedirectUri', 'oidcPostLogoutRedirectUri', 'identityManagementUrl'];
  if (config.schemaVersion !== 1 || Object.keys(config).some(key => !['schemaVersion', ...required, ...optional].includes(key))) {
    throw new Error('Unsupported UI configuration.');
  }
  for (const key of required) {
    if (typeof config[key] !== 'string' || !config[key].trim()) throw new Error('Incomplete UI configuration.');
  }
  for (const key of optional) {
    if (config[key] !== undefined && (typeof config[key] !== 'string' || !config[key].trim())) {
      throw new Error('Invalid UI configuration.');
    }
  }
  for (const key of ['identityApiBase', 'oidcAuthority', 'oidcRedirectUri', 'oidcPostLogoutRedirectUri', 'identityManagementUrl']) {
    const address = config[key] as string | undefined;
    if (address === undefined) continue;
    const relativeAllowed = key === 'identityApiBase' || key === 'identityManagementUrl';
    if (relativeAllowed && address.startsWith('/') && !address.startsWith('//') && !address.includes('\\')) continue;
    const url = new URL(address);
    const localHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    if ((url.protocol !== 'https:' && !localHttp) || url.username || url.password) {
      throw new Error('Invalid UI configuration URL.');
    }
  }
  return value as UiRuntimeConfig;
}

export function getRuntimeConfig(): UiRuntimeConfig {
  if (loadedConfig) return loadedConfig;
  // Vite removes this branch from production bundles. Keep existing local .env support.
  if (import.meta.env.DEV) {
    return {
      schemaVersion: 1,
      identityApiBase: import.meta.env.VITE_IDENTITY_API_BASE ?? '/identity/api/v1',
      oidcAuthority: import.meta.env.VITE_OIDC_AUTHORITY ?? 'https://api.one-dev.cloud.grassvalley.com/identity',
      oidcClientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? 'frontend-ui-client',
      oidcScope: import.meta.env.VITE_OIDC_SCOPE,
      oidcRedirectUri: import.meta.env.VITE_OIDC_REDIRECT_URI,
      oidcPostLogoutRedirectUri: import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI,
      identityManagementUrl: import.meta.env.VITE_IDENTITY_MANAGEMENT_URL,
    };
  }
  throw new Error('UI configuration has not been loaded.');
}

export async function loadRuntimeConfig(): Promise<void> {
  if (import.meta.env.DEV) return;
  const response = await fetch(RUNTIME_CONFIG_PATH, { cache: 'no-store', credentials: 'omit' });
  if (!response.ok || !response.headers.get('content-type')?.toLowerCase().includes('application/json')) {
    throw new Error('UI configuration could not be loaded.');
  }
  const value: unknown = await response.json();
  loadedConfig = parseRuntimeConfig(value);
}
